<script>
  // ── Die Bestandsansicht (A7/L6) ──────────────────────────────────────────
  //
  // Sie zeigt, was seit L2 fertig dasteht und von nichts angezeigt wurde.
  //
  // ZWEI STRUKTUREN, UND DAS IST BEABSICHTIGT (Plan 1.5, Vorschlag 3)
  // Breit gliedert die Liste nach Warengruppe, schmal filtert sie danach —
  // und dort ist sie GLOBAL sortiert. Dieselbe Fehlmenge kann schmal Zeile 1
  // sein und breit Zeile 14 der zweiten Gruppe; aufgefangen wird das durch den
  // Punkt im Chip. Weil die Reihenfolge und nicht nur die Gestalt sich
  // unterscheidet, lässt sich das nicht per CSS umbauen — hier entscheidet
  // deshalb ein ResizeObserver auf dem eigenen Kasten.
  //
  // ⚠ DERSELBE BRUCHPUNKT AN ZWEI STELLEN
  // Diese Ansicht misst 600 px per ResizeObserver, die Kindkomponenten fragen
  // `@container (min-width: 600px)`. Beide messen die Breite DIESES Kastens
  // (er trägt `container-type: inline-size`), fallen also zusammen — aber wer
  // die Zahl ändert, muss beide ändern. Der Viewport ist an keiner der beiden
  // Stellen im Spiel (12.1).
  import { onMount } from 'svelte';
  import BestandZeile from '../teile/BestandZeile.svelte';
  import WarengruppeFilter from '../teile/WarengruppeFilter.svelte';
  import BuchungSheet from '../teile/BuchungSheet.svelte';

  /** @type {{ onbuchen: () => void, onoeffnen: (zutatId: string) => void }} */
  let { onbuchen, onoeffnen } = $props();

  const t  = (k, p) => window.t(k, p);
  const tn = (k, n, p) => window.tn(k, n, p);

  const BRUCH = 600;

  let zeilen  = $state([]);
  let bewegungen = $state([]);
  let aktiv   = $state(/** @type {string|null} */ (null));
  // Dasselbe Sheet wie im Zutat-Detail (Plan 17.7). Es steht hier, weil eine
  // falsche Buchung meistens SOFORT auffällt — und dann steht man in der
  // Bewegungsliste und nicht im Detail einer Zutat, die man erst suchen müsste.
  let offen   = $state(/** @type {any} */ (null));
  let breit   = $state(false);
  let wurzel;

  // Die Marken der Achse kommen aus der Datenschicht, damit Liste und
  // Zutat-Detail dieselben zeigen (Plan 17.11).
  const marken = window.lagerSkalaMarken();

  /**
   * Zeichnet in dieser Menge ueberhaupt eine Zeile eine Skala? Achse und
   * Legende haengen daran: Solange jede Zeile auf `unbestimmt` steht, gibt es
   * keinen Balken — und eine Achse ueber nichts beschriftet nichts (Plan 14.4).
   * @param {any[]} zs
   */
  const hatSkala = (zs) => zs.some(
    (z) => z.prognose?.zustand === 'reicht' || z.prognose?.zustand === 'fehlt');

  // `lagerBestandsliste()` sortiert nach `dringend`; umsortiert wird hier, weil
  // es eine Frage der Ansicht ist und die Ableitung nichts davon wissen muss.
  // Schmal gibt es den Schalter nicht (2026-09-11) — dort gilt immer die
  // Grundregel, auch wenn breit vorher auf alphabetisch gestellt war.
  let sortiert = $derived(window.lagerSortiere(zeilen, breit ? sortierung : 'dringend'));
  let gruppen  = $derived(window.lagerNachWarengruppe(sortiert));
  let gefilter = $derived(window.lagerGefiltert(sortiert, aktiv));
  // Breit filtert derselbe Chip die GRUPPEN, statt zu ihnen zu springen.
  let sichtbar = $derived(aktiv ? gruppen.filter((g) => g.warengruppe === aktiv) : gruppen);

  let skalaDa    = $derived(hatSkala(zeilen));
  let fehlmengen = $derived(zeilen.filter((z) => z.prognose?.zustand === 'fehlt').length);

  // ⚠ AUS EINER BEHAUPTUNG WURDE EIN SCHALTER (Plan 17.16)
  // 17.11 hat „Fehlmengen zuerst" als HINWEIS gebaut, der die sichtbare
  // Reihenfolge beschreibt — und genau so hat er im Betrieb ausgesehen wie ein
  // Knopf, der nichts tut. Jetzt ist er einer, der etwas tut, und damit ändert
  // sich auch, was er sagen muss: Ein Schalter benennt die REGEL, die er
  // einschaltet, nicht den Zustand der Liste. „Fehlmengen zuerst" bleibt
  // deshalb auch dann richtig, wenn es gerade keine gibt.
  let sortierung = $state(/** @type {'dringend'|'alpha'} */ ('dringend'));

  const SORT_TEXT = { dringend: 'lager_meta_sort_fehlt', alpha: 'lager_meta_sort_alpha' };

  // ⚠ „Keine Zeilen" heißt zweierlei, und die beiden sehen gleich aus: Es gibt
  // wirklich nichts — oder das Journal ist noch nicht aus der lokalen
  // Datenbank zurück. Der Leerzustand ist eine AUSSAGE („fang an zu buchen")
  // und darf im zweiten Fall nicht stehen; er hat Nutzer mit vollem Lager
  // begrüßt, während ihre Zeilen noch unterwegs waren.
  let geladen = $state(!!window.lagerJournalGeladen?.());

  function neu() {
    geladen = !!window.lagerJournalGeladen?.();
    zeilen = window.lagerMitGruppe();
    bewegungen = window.lagerBewegungen(12);
    // Eine Gruppe, die es nicht mehr gibt, darf den Filter nicht blind machen.
    if (aktiv && !zeilen.some((z) => z.anzeigeGruppe === aktiv)) aktiv = null;
    // Eine zurückgenommene Buchung verschwindet aus dem Sheet nicht, sie ändert
    // ihren Zustand — also die frische Fassung nachziehen (wie im Detail).
    if (offen) offen = window.lagerBewegungBuchung(bewegungen.find((b) => b.id === offen.id));
  }

  onMount(() => {
    neu();

    // Einmal messen, bevor beobachtet wird: Sonst begänne die Ansicht immer
    // schmal und klappte auf dem Schreibtisch sichtbar um.
    breit = wurzel.getBoundingClientRect().width >= BRUCH;

    // ⚠ Die Umschaltung MUSS aus der Zustellung des Observers heraus verschoben
    // werden. Sie tauscht die Liste gegen die Gruppen, das ändert die Höhe
    // dieses Kastens, und die Änderung erzeugt neue Meldungen im selben
    // Durchlauf — der Browser meldet dann „ResizeObserver loop completed with
    // undelivered notifications", und der zentrale Error-Handler der App macht
    // daraus einen Fehler-Toast. Ein rAF legt die Änderung hinter den Durchlauf;
    // der Vergleich davor sorgt dafür, dass eine reine Höhenänderung (etwa beim
    // Filtern) gar nicht erst etwas anstößt.
    let zuletzt = breit;
    const beo = new ResizeObserver(([e]) => {
      const b = e.contentRect.width >= BRUCH;
      if (b === zuletzt) return;
      zuletzt = b;
      requestAnimationFrame(() => { breit = b; });
    });
    beo.observe(wurzel);
    document.addEventListener('lager:journal-geaendert', neu);
    document.addEventListener('zutat-stamm:changed', neu);
    // Ein geplanter Backtag ist Bedarf, auch ohne Buchung (A7/L5).
    document.addEventListener('backtage:geaendert', neu);
    return () => {
      beo.disconnect();
      document.removeEventListener('lager:journal-geaendert', neu);
      document.removeEventListener('zutat-stamm:changed', neu);
      document.removeEventListener('backtage:geaendert', neu);
    };
  });

  /** @param {number} gramm */
  function summe(gramm) {
    const m = window.lagerMenge(gramm);
    return `${m.wert} ${m.einheit}`;
  }

  /** Vorzeichenbehaftet, damit ein Eingang als Zuwachs lesbar ist. */
  function delta(gramm) {
    const m = window.lagerMenge(Math.abs(gramm));
    return `${gramm < 0 ? '−' : '+'}${m.wert} ${m.einheit}`;
  }

  /** Tages-Zwischenanker der Bewegungsliste. */
  function tag(ts) {
    return new Date(ts).toLocaleDateString(window.currentLocale(),
      { weekday: 'long', day: 'numeric', month: 'long' });
  }

  // ── Der Tooltip hinter „Reichweite" (2026-09-11) ─────────────────────────
  // Er trägt den Satz, der vorher als eigene Zeile über der Liste stand: „2
  // Backtage" meint Backtage MIT DIESER ZUTAT (Plan 19.8). Die Auskunft braucht
  // man beim ersten Mal, danach nicht mehr — also steht sie da, wo man nach
  // ihr fragt, statt dauerhaft Platz über der Liste zu belegen.
  //
  // ⚠ ER STEHT EINMAL AUF DER ANSICHT, NICHT IN DER KARTE.
  // `.lg-karte` schneidet mit `overflow: hidden` ab (runde Ecken über den
  // getönten Zeilen) — ein Tooltip darin würde bei einer Gruppe mit nur einer
  // Zeile mittendrin gekappt. Gesetzt wird er relativ zu `.lg-bestand`; der
  // Kasten ist wegen `container-type` ohnehin Bezugsrahmen seiner
  // positionierten Nachfahren.
  let tipp = $state(/** @type {{ top: number, left: number, gruppe: string, fest: boolean } | null} */ (null));

  /**
   * @param {HTMLElement} knopf
   * @param {string} gruppe
   * @param {boolean} fest per Klick gesetzt — bleibt stehen, wenn der Zeiger geht
   */
  function zeigeTipp(knopf, gruppe, fest) {
    const k = knopf.getBoundingClientRect();
    const w = wurzel.getBoundingClientRect();
    tipp = {
      top: k.bottom - w.top + 6,
      left: Math.max(8, k.left - w.left - 10),
      gruppe,
      fest: fest || (tipp?.gruppe === gruppe && tipp.fest),
    };
  }

  /** @param {boolean} [nurLose] einen per Klick gesetzten stehen lassen */
  function verbergeTipp(nurLose = false) {
    if (tipp && !(nurLose && tipp.fest)) tipp = null;
  }

  /**
   * Der Klick ist auf Touch der einzige Weg zum Tooltip — ein Überfahren gibt
   * es dort nicht. Ein zweiter Klick nimmt ihn wieder weg.
   * @param {HTMLElement} knopf
   * @param {string} gruppe
   */
  function schalteTipp(knopf, gruppe) {
    if (tipp?.fest && tipp.gruppe === gruppe) tipp = null;
    else zeigeTipp(knopf, gruppe, true);
  }

  // ⚠ DIE ÜBERSCHRIFT TEILT SICH DIE ZEILE MIT DEN MARKEN.
  // „Reichweite" steht am linken Ende der Skala, die Marke „2" auf 20 % — bei
  // einer schmalen Skala (Kasten zwischen 600 und ~940 px) läge sie darunter.
  // Wo genau, hängt an der Schriftbreite und damit an der Sprache („Coverage"
  // ist kürzer); eine feste Container-Query wäre in einer der beiden falsch.
  // Also wird gemessen: Eine Beschriftung, die an die Überschrift oder an die
  // letzte Marke (die mit der Einheit) stößt, tritt zurück. Der Strich bleibt.
  // `visibility` statt `display`: Die Box bleibt vermessbar und die Achse
  // behält ihre Höhe — der Observer kann sich nicht selbst anstoßen.
  /** @param {HTMLElement} skala */
  function freistellen(skala) {
    const LUFT = 8;
    const titel = skala.querySelector('.lg-achse__titel');
    const pruefen = () => {
      const marken = [...skala.querySelectorAll('.lg-achse__marke')];
      const letzte = marken.at(-1);
      if (!titel || !letzte) return;
      const links  = titel.getBoundingClientRect().right + LUFT;
      const rechts = letzte.getBoundingClientRect().left - LUFT;
      for (const m of marken.slice(0, -1)) {
        const r = m.getBoundingClientRect();
        m.classList.toggle('is-verdeckt', r.left < links || r.right > rechts);
      }
    };
    const beo = new ResizeObserver(pruefen);
    beo.observe(skala);
    // Auch die Überschrift: Sie wird breiter, sobald die Schrift geladen ist.
    if (titel) beo.observe(titel);
    return { destroy: () => beo.disconnect() };
  }
</script>

<svelte:window
  onpointerdown={(e) => {
    if (!(/** @type {Element} */ (e.target)).closest?.('.lg-info')) verbergeTipp();
  }}
  onkeydown={(e) => { if (e.key === 'Escape') verbergeTipp(); }}
  onresize={() => verbergeTipp()}
/>

<div class="lg-bestand" bind:this={wurzel}>
  <header class="lg-kopf">
    <div class="lg-kopf__text">
      <h1 class="lg-titel">{t('lager_titel')}</h1>
      <span class="lg-beta">{t('lager_beta')}</span>
      {#if zeilen.length}
        <!-- Die Zahl der Fehlmengen stand bis 2026-09-11 in einer eigenen
             Meta-Zeile über der Liste. Hier gehört sie zu der Aussage, die sie
             ergänzt: wie viel da ist, und wie viel davon nicht reicht. -->
        <p class="lg-sub">
          {tn('lager_bestand_sub', zeilen.length)}
          {#if fehlmengen}
            <span class="lg-sub__fehlt">{tn('lager_meta_fehlt', fehlmengen)}</span>
          {/if}
        </p>
      {/if}
    </div>
    <!-- Der CTA steht breit im KOPF und schmal in der Fußleiste — beides ist
         gezeichnet: bestand.html hat ihn oben rechts, bestand-mobil.html in der
         Daumenzone. Ein klebender Balken über einer Liste, die man am
         Schreibtisch liest, verdeckte nur eine Zeile. -->
    {#if zeilen.length}
      <button type="button" class="lg-haupt lg-haupt--kopf" onclick={onbuchen}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"
             stroke-linecap="round" width="18" height="18" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        {t('lager_eingang_oeffnen')}
      </button>
    {/if}
  </header>

  {#if zeilen.length === 0 && !geladen}
    <!-- Noch nichts zu sagen. Bewusst kein Platzhalter-Gerüst: Die lokale
         Datenbank antwortet in Millisekunden, ein Skelett wäre nur ein
         zweites Aufblitzen. Dauert es doch, steht hier ruhig nichts. -->
  {:else if zeilen.length === 0}
    <!-- Erstnutzung. Die erste Woche eines Testers ist zwangsläufig karg —
         das gehört gesagt, nicht kaschiert. -->
    <div class="lg-leer">
      <h2 class="lg-leer__titel">{t('lager_leer_titel')}</h2>
      <p class="lg-leer__text">{t('lager_leer_text')}</p>
      <button type="button" class="lg-haupt" onclick={onbuchen}>{t('lager_leer_cta')}</button>
    </div>
  {:else}
    <!-- ⚠ KEINE META-ZEILE MEHR ÜBER DER LISTE (Betreiber, 2026-09-11).
         Zahl der Fehlmengen, Legende, Basis-Satz und Sortierschalter (Plan
         17.1, 19.8) belegten zusammen zwei Zeilen über der Tabelle — für
         Auskünfte, die man einmal braucht. Sie stehen jetzt dort, wo man sie
         sucht: die Zahl im Kopf, der Schalter rechts in der Filterleiste,
         der Basis-Satz hinter „Reichweite" auf der Achse, die Legende unter
         der Liste. -->
    <WarengruppeFilter
      gruppen={gruppen}
      gesamt={zeilen.length}
      {aktiv}
      onwaehlen={(g) => (aktiv = g)}
    >
      <!-- Nur breit: Schmal steht die Liste immer nach der Grundregel, und
           ein Schalter in der ohnehin knappen Leiste nähme den Chips den
           Platz. Er hängt weiter an einer gezeichneten Skala — ohne Prognose
           gibt es keine Fehlmengen, die zuerst stehen könnten. -->
      {#if breit && skalaDa}
        <button
          type="button"
          class="lg-sort"
          aria-label={t('lager_meta_sort_wechseln')}
          onclick={() => (sortierung = sortierung === 'dringend' ? 'alpha' : 'dringend')}
        >
          <!-- ⚠ KEIN PFEIL-SYMBOL (Betreiber, 2026-09-10).
               Hier stand das uebliche Auf-und-Ab zweier Pfeile — das Zeichen
               fuer eine Richtung, die sich umkehren laesst. Genau die gibt es
               nicht: Der Schalter wechselt zwischen ZWEI REGELN („Fehlmengen
               zuerst" und „alphabetisch"), und jede sortiert in genau einer
               Richtung. Das Symbol versprach eine Bedienung, die es nicht gibt.
               Der Text benennt die Regel und genuegt. -->
          {t(SORT_TEXT[sortierung])}
        </button>
      {/if}
    </WarengruppeFilter>

    {#if breit}
      {#each sichtbar as g (g.warengruppe)}
        <section class="lg-gruppe">
          <div class="lg-gruppe__kopf">
            <span class="lg-gruppe__name">{g.warengruppe}</span>
            <span class="lg-gruppe__anzahl">{tn('lager_gruppe_zutaten', g.zeilen.length)}</span>
            <span class="lg-gruppe__summe">{summe(g.summe)}</span>
          </div>
          <div class="lg-karte">
            <!-- Einmal je Karte, nicht je Zeile: 27-mal dieselbe Beschriftung
                 waere Rauschen. Sie traegt dasselbe Raster wie die Zeile
                 darunter — beide aus --lg-zeilen-raster.

                 Seit 2026-09-11 hat die mittlere Spalte eine Überschrift wie
                 die beiden anderen. Vorher standen dort nur Zahlen und am
                 Ende „Backtage" — eine Spalte, von der man nicht wusste, was
                 sie misst. Die Marken bleiben für Screenreader verborgen; der
                 Satz in jeder Zeile sagt dasselbe in Worten. -->
            {#if hatSkala(g.zeilen)}
              <div class="lg-achse">
                <span class="lg-achse__kopf" aria-hidden="true">{t('lager_achse_zutat')}</span>
                <span class="lg-achse__skala" use:freistellen>
                  <span class="lg-achse__titel">
                    {t('lager_achse_reich')}
                    <button
                      type="button"
                      class="lg-info"
                      aria-label={t('lager_achse_info')}
                      aria-describedby="lg-tipp"
                      onmouseenter={(e) => zeigeTipp(e.currentTarget, g.warengruppe, false)}
                      onmouseleave={() => verbergeTipp(true)}
                      onfocus={(e) => zeigeTipp(e.currentTarget, g.warengruppe, false)}
                      onblur={() => verbergeTipp()}
                      onclick={(e) => schalteTipp(e.currentTarget, g.warengruppe)}
                    >
                      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4"/>
                        <line x1="8" y1="7" x2="8" y2="11.5" stroke="currentColor"
                              stroke-width="1.6" stroke-linecap="round"/>
                        <circle cx="8" cy="4.6" r="0.95" fill="currentColor"/>
                      </svg>
                    </button>
                  </span>
                  {#each marken as m (m.wert)}
                    <i style="left: {m.prozent}%" aria-hidden="true"></i>
                    <span style="left: {m.prozent}%" class="lg-achse__marke"
                          class:is-letzte={m.letzte} aria-hidden="true">{m.label}</span>
                  {/each}
                </span>
                <span class="lg-achse__menge" aria-hidden="true">{t('lager_achse_bestand')}</span>
              </div>
            {/if}
            {#each g.zeilen as z (z.zutatId)}
              <BestandZeile zeile={z} onoeffnen={() => onoeffnen(z.zutatId)} />
            {/each}
          </div>
        </section>
      {/each}
    {:else}
      <div class="lg-liste">
        {#each gefilter as z (z.zutatId)}
          <BestandZeile zeile={z} onoeffnen={() => onoeffnen(z.zutatId)} />
        {/each}
      </div>
    {/if}

    <!-- Die Legende UNTER der Liste (2026-09-11): Sie erklärt die Gestalt der
         Skala, und dafür schaut man hin, wenn man die Balken gesehen hat —
         nicht bevor. Zwei Einträge statt der drei des Kits: Die Fehlmenge
         erklärt sich auf der Zeile selbst (getönter Grund UND Satz in
         Worten), der 2-px-Strich der Plan-Marke nirgends.

         Schmal steht der Basis-Satz mit dabei: Dort gibt es keine Achse und
         damit kein „Reichweite", hinter dem er als Tooltip stehen könnte.
         Breit steht er NUR im Tooltip — nie zweimal (Plan 19.9). -->
    {#if skalaDa}
      <div class="lg-fussnote">
        <span class="lg-legende">
          <span><i></i>{t('lager_legende_reich')}</span>
          <span><i class="is-plan"></i>{t('lager_legende_plan')}</span>
        </span>
        {#if !breit}<p class="lg-basis">{t('lager_legende_basis')}</p>{/if}
      </div>
    {/if}

    <!-- Im Entwurf Beiwerk, in L6 das tragende Element: Es erklärt, warum
         jede Zeile auf „noch keine" steht (Plan 14.4). -->
    <p class="lg-note">{@html t('lager_note')}</p>

    {#if breit && bewegungen.length}
      <!-- Priorität B. Schmal fehlt sie noch: Der Kopfknopf des Entwurfs
           führte in eine Ansicht, die nicht gezeichnet ist — ihren Ort
           bekommt sie mit dem Zutat-Detail in L7 (Plan 14.5). -->
      <h2 class="lg-abschnitt">{t('lager_bew_titel')}</h2>
      <div class="lg-karte lg-karte--bew">
        {#each bewegungen as b, i (b.id)}
          {#if i === 0 || tag(b.ts) !== tag(bewegungen[i - 1].ts)}
            <div class="lg-tag"><span>{tag(b.ts)}</span><i></i></div>
          {/if}
          <!-- Die ganze Zeile ist die Trefferfläche (Fitts) — dasselbe Prinzip
               wie im Verlauf des Zutat-Details. -->
          <button type="button" class="lg-bew" class:is-storno={b.storniert}
                  onclick={() => (offen = window.lagerBewegungBuchung(b))}>
            <!-- ⚠ EINE ZÄHLUNG IST WEDER ZUGANG NOCH VERBRAUCH (US-4).
                 Grün heißt in diesem Modul „ist hereingekommen". Eine Inventur
                 mit positiver Differenz wäre damit ein Wareneingang, den es nie
                 gab — und eine mit negativer läse sich als verbacken. Zeichen
                 und Betrag bleiben deshalb neutral; das Vorzeichen sagt weiter
                 die Richtung, der Titel sagt, woher sie kommt. -->
            <span class="lg-bew__zeichen"
                  class:is-ein={b.delta >= 0 && b.art !== 'inventur'} aria-hidden="true">
              {#if b.art === 'inventur'}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
                     stroke-linecap="round" stroke-linejoin="round" width="13" height="13">
                  <path d="M12 3v18"/><path d="M5 7h14"/>
                  <path d="M5 7 2 13a3 3 0 0 0 6 0Z"/><path d="M19 7l-3 6a3 3 0 0 0 6 0Z"/>
                </svg>
              {:else}
                {b.delta < 0 ? '−' : '+'}
              {/if}
            </span>
            <span class="lg-bew__text">
              <span class="lg-bew__titel">{b.titel}</span>
              <span class="lg-bew__meta">{b.meta}</span>
            </span>
            <span class="lg-bew__delta"
                  class:is-ein={b.delta >= 0 && b.art !== 'inventur'}>{delta(b.delta)}</span>
          </button>
        {/each}
      </div>
    {/if}
  {/if}

  <!-- Der eine Tooltip für alle Achsen — warum er hier steht und nicht in
       der Karte, steht bei `tipp` im Skript. -->
  {#if tipp && breit && skalaDa}
    <div id="lg-tipp" class="lg-tipp" role="tooltip"
         style="top: {tipp.top}px; left: {tipp.left}px">{t('lager_legende_basis')}</div>
  {/if}

  <!-- Die häufigste Handlung gehört in die Daumenzone. `sticky`, nicht
       `fixed`: Nichts Ein-/Ausblendendes darf in die Topbar-Höhenmessung
       geraten, und das Panel bleibt bewusst KEIN Scroll-Container (14.8).
       Im Leerzustand entfällt sie: Der Erstnutzungs-Kasten trägt den einzigen
       Knopf, den es dort geben soll. -->
  {#if zeilen.length}
  <div class="lg-fuss">
    <button type="button" class="lg-haupt lg-haupt--breit" onclick={onbuchen}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"
           stroke-linecap="round" width="18" height="18" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      {t('lager_eingang_oeffnen')}
    </button>
  </div>
  {/if}
</div>

{#if offen}
  <BuchungSheet
    buchung={offen}
    onschliessen={() => (offen = null)}
    onzurueckgenommen={() => (offen = null)}
  />
{/if}

<style>
  /* Der Kasten, dessen Breite über alles entscheidet — Kindkomponenten fragen
     ihn per @container, diese Ansicht misst ihn per ResizeObserver. */
  .lg-bestand {
    container-type: inline-size;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 0 24px;
  }

  .lg-kopf { padding: 20px 16px 12px; }
  .lg-kopf__text { min-width: 0; }
  /* Zwei Klassen, nicht eine: `.lg-haupt` steht weiter unten im Stylesheet und
     setzt `display: inline-flex`. Bei gleicher Spezifitaet gewaenne die
     spaetere Regel — der Kopf-Knopf waere schmal sichtbar und der CTA staende
     doppelt auf der Seite. (Dieselbe Falle wie `.hidden` gegen `.tab-dirty-dot`
     in CLAUDE.md, nur innerhalb einer Datei.) */
  .lg-kopf .lg-haupt--kopf { display: none; }
  .lg-titel {
    display: inline;
    margin: 0 8px 0 0;
    font-size: 22px; font-weight: 700; letter-spacing: -0.02em;
    color: var(--lg-ink);
  }
  .lg-beta {
    display: inline-flex; align-items: center; vertical-align: 3px;
    padding: 2px 8px; border-radius: 999px;
    background: var(--lg-tint); border: 1px solid var(--lg-line);
    font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-sub {
    max-width: 56ch; margin: 8px 0 0;
    font-size: 13px; line-height: 1.5; color: var(--lg-ink-soft);
  }

  .lg-liste, .lg-karte { background: var(--lg-card); overflow: hidden; }
  .lg-liste { border-top: 1px solid var(--lg-line); }

  .lg-gruppe { margin-top: 20px; }
  .lg-gruppe__kopf {
    display: flex; align-items: baseline; gap: 12px;
    padding: 0 20px 8px;
  }
  .lg-gruppe__name {
    font-size: 12px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-soft);
  }
  .lg-gruppe__anzahl { font-size: 11.5px; color: var(--lg-ink-mute); }
  .lg-gruppe__summe {
    margin-left: auto; font-size: 13px; font-weight: 700;
    color: var(--lg-ink-soft); font-variant-numeric: tabular-nums;
  }

  /* Die einzige Farbe im Modul, die eine AUSSAGE traegt — hier zaehlt sie,
     was die getoenten Zeilen weiter unten einzeln zeigen. Der Punkt ist
     derselbe wie im Filter-Chip: Er verbindet die Zahl mit der Gruppe, in
     der die Fehlmenge steckt. */
  /* ⚠ KEIN inline-flex, obwohl es hier nach einem Fall dafuer aussieht.
     Ein inline-flex-Kasten nimmt seine Grundlinie vom ERSTEN Flex-Element —
     das ist der leere Punkt, und ein leerer Kasten hat keine Schrift, also
     gilt seine Unterkante. Der Text im Kasten stand dadurch 2,75 px ueber der
     Zeile, in der er steht (Betrieb 2026-09-15). Als normaler Inline-Text
     teilt er die Grundlinie des Satzes davor, und nur der Punkt richtet sich
     aus: `middle` setzt ihn auf die halbe x-Hoehe, also optisch mittig zur
     Kleinbuchstabenhoehe. */
  .lg-sub__fehlt {
    margin-left: 6px; white-space: nowrap;
    font-weight: 600; color: var(--lg-fehlt-ink);
  }
  .lg-sub__fehlt::before {
    content: ''; display: inline-block; vertical-align: middle;
    width: 6px; height: 6px; margin-right: 5px; border-radius: 50%;
    background: var(--lg-fehlt-edge);
  }

  /* ── Sortierschalter, rechts in der Filterleiste ─────────────────────────
     Ein Schalter, kein Etikett — deshalb sieht er auch wie einer aus: eigene
     Flaeche beim Ueberfahren, Fokusring, so hoch wie die Chips daneben. Die
     Form ist die des Filter-Chips, nur leiser (kein Rand in Ruhe). Er wird
     hier gestaltet, weil er hier geschrieben ist — die Leiste reicht ihn nur
     durch. */
  .lg-sort {
    flex-shrink: 0;
    display: inline-flex; align-items: center; gap: 5px;
    margin-left: auto; min-height: 34px; padding: 0 12px;
    border: 1px solid transparent; border-radius: 999px;
    background: none; font: inherit; font-size: 12px; font-weight: 600;
    color: var(--lg-ink-mittel); white-space: nowrap; cursor: pointer;
  }
  .lg-sort:hover { background: var(--lg-card); color: var(--lg-ink); }
  .lg-sort:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }

  /* ── Unter der Liste: Legende (und schmal der Basis-Satz) ────────────── */
  .lg-fussnote {
    margin: 14px 16px 0;
    font-size: 11.5px; color: var(--lg-ink-mute);
  }
  .lg-legende { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; }
  .lg-legende > span { display: inline-flex; align-items: center; gap: 6px; }
  .lg-legende i {
    width: 18px; height: 4px; border-radius: 2px;
    background: var(--lg-fill);
  }
  /* Dieselbe Farbe wie die Marke, die sie erklaert — die des Kits (stone-600)
     ist eine andere als die gezeichnete (stone-700), und eine Legende, die
     danebenliegt, ist schlimmer als keine. */
  .lg-legende i.is-plan {
    width: 2px; height: 12px; border-radius: 0;
    background: var(--lg-plan);
  }
  .lg-basis {
    margin: 6px 0 0;
    font-size: 11px;
    line-height: 1.45;
  }
  /* Der Hinweistext direkt darunter braucht dann keinen vollen Abstand mehr. */
  .lg-fussnote + .lg-note { margin-top: 12px; }

  /* ── Der Tooltip hinter „Reichweite" ─────────────────────────────────────
     Positioniert relativ zu `.lg-bestand`, über allem der Ansicht bis auf
     nichts: Filterleiste (5) und Fußleiste (6) liegen darunter. */
  .lg-tipp {
    position: absolute;
    z-index: 7;
    max-width: 280px;
    padding: 8px 11px;
    border-radius: 8px;
    background: var(--lg-ink);
    color: #fff;
    font-size: 12px; font-weight: 500; line-height: 1.45;
    letter-spacing: 0; text-transform: none;
    box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.35);
    pointer-events: none;
  }

  /* ── Achse ueber der Karte ───────────────────────────────────────────────
     Nur breit — sie sitzt im Raster der Zeile, das es schmal nicht gibt. */
  .lg-achse { display: none; }

  .lg-note {
    display: block; margin: 20px 16px 0; padding: 12px 14px;
    background: var(--lg-tint); border: 1px solid var(--lg-line);
    border-radius: 10px;
    font-size: 12px; line-height: 1.5; color: var(--lg-ink-soft);
  }

  .lg-abschnitt {
    margin: 28px 20px 8px;
    font-size: 11px; font-weight: 700; letter-spacing: 0.09em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  /* Das Polster sitzt seit L7 an den ZEILEN, nicht an der Karte: Sie sind
     Knöpfe geworden, und ihre Fläche soll beim Überfahren bis an den Rand der
     Karte reichen — so wie im Verlauf des Zutat-Details (`.lg-buch`). */
  .lg-karte--bew { padding: 4px 0 12px; }
  .lg-tag { display: flex; align-items: center; gap: 10px; padding: 14px 20px 6px; }
  .lg-tag span {
    font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-tag i { flex: 1; height: 1px; background: var(--lg-line-fein); }
  .lg-bew {
    display: grid; grid-template-columns: 26px minmax(0, 1fr) auto;
    align-items: center; gap: 12px;
    width: 100%; box-sizing: border-box; padding: 8px 20px;
    border: 0; background: none; font: inherit; text-align: left; cursor: pointer;
  }
  .lg-bew + .lg-bew { border-top: 1px solid var(--lg-line-fein); }
  .lg-bew:hover { background: var(--lg-tint); }
  .lg-bew:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }

  /* Der Storno ist ein ZUSTAND der Zeile, keine zweite Zeile (Plan 17.3) —
     dieselbe Darstellung wie im Verlauf des Zutat-Details. */
  .lg-bew.is-storno { opacity: 0.62; }
  .lg-bew.is-storno .lg-bew__titel,
  .lg-bew.is-storno .lg-bew__delta { text-decoration: line-through; }
  .lg-bew.is-storno .lg-bew__delta { color: var(--lg-ink-mute); }
  .lg-bew.is-storno .lg-bew__zeichen { background: var(--lg-tick); color: var(--lg-ink-mute); }
  .lg-bew__zeichen {
    width: 26px; height: 26px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    background: var(--lg-tick); color: var(--lg-ink-soft);
    font-weight: 700; font-size: 14px;
  }
  .lg-bew__zeichen.is-ein { background: var(--lg-ein-bg); color: var(--lg-ein-ink); }
  .lg-bew__text { min-width: 0; }
  .lg-bew__titel {
    display: block; font-size: 13.5px; font-weight: 600; color: var(--lg-ink);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .lg-bew__meta { display: block; font-size: 11.5px; color: var(--lg-ink-mute); }
  .lg-bew__delta {
    font-size: 14px; font-weight: 700; color: var(--lg-ink-soft);
    font-variant-numeric: tabular-nums; white-space: nowrap;
  }
  .lg-bew__delta.is-ein { color: var(--lg-ein-ink); }

  /* ── Erstnutzung ──────────────────────────────────────────────────────── */
  .lg-leer {
    margin: 8px 16px; padding: 32px 20px; text-align: center;
    background: var(--lg-card); border: 1px solid var(--lg-line);
    border-radius: 14px;
  }
  .lg-leer__titel { margin: 0; font-size: 17px; font-weight: 700; color: var(--lg-ink); }
  .lg-leer__text {
    max-width: 42ch; margin: 8px auto 20px;
    font-size: 13px; line-height: 1.55; color: var(--lg-ink-soft);
  }

  /* ── Fußleiste ────────────────────────────────────────────────────────── */
  .lg-fuss {
    position: sticky;
    bottom: 0;
    /* ⚠ DER Z-INDEX IST TRAGEND, NICHT KOSMETIK (aus dem Betrieb, 2026-09-10).
       Ohne ihn war der CTA schmal sichtbar, aber nicht bedienbar: Der Griff
       öffnete das Detail der Zutat, die HINTER ihm lag. Die Trefferflächen der
       Zeilen (`.lg-zeile__hit`, z-index 2) sind positioniert; in derselben
       Stapelebene wird JEDER positive z-index nach allen `auto`-Elementen
       gezeichnet — die Reihenfolge im DOM hilft dann nicht mehr, und die
       klebende Fußleiste hatte gar keinen. Der Wert liegt über dem der
       Filterleiste (5), weil diese Leiste die unterste Schicht der Ansicht
       überdeckt und nicht umgekehrt. */
    z-index: 6;
    margin-top: 20px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    background: var(--lg-app-glas);
    backdrop-filter: blur(14px);
    border-top: 1px solid var(--lg-line);
  }
  .lg-haupt {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 48px; padding: 0 18px; border: 0; border-radius: 10px;
    background: var(--lg-accent); color: #fff; font: inherit;
    font-size: 15px; font-weight: 700; letter-spacing: -0.01em; cursor: pointer;
    box-shadow: 0 6px 16px -8px rgba(192, 90, 62, 0.5);
  }
  .lg-haupt--breit { width: 100%; }
  .lg-haupt:hover { background: var(--lg-accent-hover); }
  .lg-haupt:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }

  @container (min-width: 600px) {
    .lg-kopf {
      display: flex; align-items: flex-start; gap: 16px;
      padding: 24px 20px 14px;
    }
    .lg-kopf .lg-haupt--kopf { display: inline-flex; margin-left: auto; flex-shrink: 0; }
    .lg-fuss { display: none; }
    .lg-titel { font-size: 26px; }
    .lg-liste, .lg-karte {
      border: 1px solid var(--lg-line);
      border-radius: 14px;
      margin: 0 20px;
    }
    .lg-note { margin: 24px 20px 0; }
    .lg-fussnote { margin: 14px 20px 0; }
    .lg-fussnote + .lg-note { margin-top: 12px; }

    .lg-achse {
      display: grid;
      grid-template-columns: var(--lg-zeilen-raster);
      gap: 0 var(--lg-zeilen-abstand);
      align-items: center;
      padding: 10px 20px 4px;
    }
    .lg-achse__kopf, .lg-achse__menge {
      font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--lg-ink-mute);
    }
    .lg-achse__menge { text-align: right; }
    /* 18 px statt der 14 des Kits, und der Strich sitzt UNTER der Zahl statt
       hinter ihr. Im Kit liegen beide auf derselben Hoehe: Weil die Zahl auf
       ihrer Marke zentriert steht, verdeckt sie den Strich vollstaendig —
       sichtbar bleibt nur der letzte, und der klebt am „e" von „Backtage",
       weil DIE Zahl nach links ausgerueckt ist. Beides im Pruefblatt zu
       L7/1 gesehen. Getrennt sagen beide, was sie sagen sollen. */
    .lg-achse__skala { position: relative; height: 18px; }
    /* Der Strich in der Farbe des Balkens, nicht in der der Linien: Ein
       1 px breiter Strich in --lg-line ist auf weissem Grund unsichtbar —
       genau der Fehler, der in L6 schon einmal die Fortschrittsbalken
       verschluckt hat (Plan 14.16). */
    .lg-achse__skala i {
      position: absolute; top: 13px;
      width: 1px; height: 5px; background: var(--lg-fill);
    }
    .lg-achse__marke {
      position: absolute; top: 0; white-space: nowrap;
      font-size: 10px; font-weight: 600; letter-spacing: 0.04em;
      color: var(--lg-ink-mute); transform: translateX(-50%);
    }
    /* Die letzte Marke sitzt auf 100 % und raegte sonst zur Haelfte in die
       Mengenspalte. */
    .lg-achse__marke.is-letzte { transform: translateX(-100%); }
    /* Gesetzt von `freistellen()` im Skript — deshalb :global, Svelte sähe
       die Klasse im Markup nicht und würfe die Regel sonst weg. */
    .lg-achse__marke:global(.is-verdeckt) { visibility: hidden; }

    /* Die Überschrift der mittleren Spalte, in derselben Schrift wie „Zutat"
       und „Bestand" — am linken Ende der Skala, wo sie beginnt. */
    .lg-achse__titel {
      position: absolute; left: 0; top: 0;
      display: inline-flex; align-items: center; gap: 4px;
      height: 13px; white-space: nowrap;
      font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--lg-ink-mute);
    }
    /* 13 px Symbol, aber eine Trefferfläche von gut 25 px: Die ::after-Fläche
       reicht über den Knopf hinaus, ohne die Zeile höher zu machen. */
    .lg-info {
      position: relative;
      display: inline-flex; align-items: center; justify-content: center;
      width: 15px; height: 15px; padding: 0;
      border: 0; border-radius: 50%; background: none;
      color: var(--lg-ink-mute); cursor: help;
    }
    .lg-info::after { content: ''; position: absolute; inset: -6px; }
    .lg-info:hover { color: var(--lg-ink); }
    .lg-info:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 1px; }
    /* Der CTA ist auf dieser Breite kein Daumenziel mehr — er bleibt in der
       Leiste, nimmt aber nur seine eigene Breite ein. */
    .lg-fuss { padding: 14px 20px; text-align: right; }
    .lg-haupt--breit { width: auto; }
  }
</style>
